import { useRef, useState, useCallback } from 'react';
import { api } from '@/lib/apiClient';
import type { MaterialInventory, RawMaterial } from '@/types/legacy';

export interface BarcodeScanResult {
  code: string;
  quantity: number;
  raw: string;
}

export interface LookupResult {
  found: boolean;
  source: 'material_inventory' | 'raw_materials' | null;
  material: MaterialInventory | null;
  rawMaterial: RawMaterial | null;
}

interface UseBarcodeScanner {
  isScanning: boolean;
  lastScan: BarcodeScanResult | null;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement>;
  handleKeyboardScan: (value: string) => void;
  lookupMaterial: (code: string) => Promise<LookupResult>;
  clearScan: () => void;
  clearError: () => void;
}

/**
 * Parsea un código de barras con formato: CODIGO_PRODUCTO|CANTIDAD
 * Si no tiene separador, solo devuelve el código con cantidad 0
 */
export function parseBarcode(raw: string): BarcodeScanResult | null {
  if (!raw || raw.trim().length === 0) {
    return null;
  }

  const trimmed = raw.trim();
  const parts = trimmed.split('|');

  if (parts.length === 2) {
    const code = parts[0].trim();
    const quantity = parseFloat(parts[1].trim());

    if (code.length === 0) {
      return null;
    }

    return {
      code,
      quantity: isNaN(quantity) ? 0 : quantity,
      raw: trimmed,
    };
  }

  // Sin separador, solo es el código
  return {
    code: trimmed,
    quantity: 0,
    raw: trimmed,
  };
}

/**
 * Hook para manejar escaneo de códigos de barras
 * Soporta lectores USB (entrada de teclado) y cámara
 */
export function useBarcodeScanner(): UseBarcodeScanner {
  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState<BarcodeScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Buffer para detectar entrada rápida de scanner USB
  const inputBuffer = useRef<string>('');
  const lastKeyTime = useRef<number>(0);
  const scanTimeout = useRef<NodeJS.Timeout | null>(null);

  /**
   * Maneja la entrada del teclado para detectar scanner USB
   * Los scanners USB envían caracteres muy rápido (<50ms entre caracteres)
   */
  const handleKeyboardScan = useCallback((value: string) => {
    const now = Date.now();
    const timeDiff = now - lastKeyTime.current;

    // Si hay más de 100ms entre caracteres, reiniciar buffer
    if (timeDiff > 100) {
      inputBuffer.current = '';
    }

    inputBuffer.current = value;
    lastKeyTime.current = now;

    // Limpiar timeout anterior
    if (scanTimeout.current) {
      clearTimeout(scanTimeout.current);
    }

    // Esperar un momento para asegurarse de que el scan está completo
    scanTimeout.current = setTimeout(() => {
      const scannedValue = inputBuffer.current.trim();
      if (scannedValue.length > 0) {
        setIsScanning(true);
        const result = parseBarcode(scannedValue);

        if (result) {
          setLastScan(result);
          setError(null);
        } else {
          setError('Código de barras inválido');
        }

        setIsScanning(false);
        inputBuffer.current = '';
      }
    }, 150);
  }, []);

  /**
   * Busca un material por código en material_inventory y raw_materials
   */
  const lookupMaterial = useCallback(async (code: string): Promise<LookupResult> => {
    const emptyResult: LookupResult = {
      found: false,
      source: null,
      material: null,
      rawMaterial: null,
    };

    if (!code || code.trim().length === 0) {
      return emptyResult;
    }

    try {
      // Buscar en material_inventory primero
      const { data: materials, error: matError } = await api
        .from('material_inventory')
        .select('*')
        .eq('code', code.trim())
        .eq('is_active', true);

      if (!matError && materials && materials.length > 0) {
        return {
          found: true,
          source: 'material_inventory',
          material: materials[0] as MaterialInventory,
          rawMaterial: null,
        };
      }

      // Buscar en raw_materials
      const { data: rawMaterials, error: rawError } = await api
        .from('raw_materials')
        .select('*')
        .eq('code', code.trim())
        .eq('is_active', true);

      if (!rawError && rawMaterials && rawMaterials.length > 0) {
        return {
          found: true,
          source: 'raw_materials',
          rawMaterial: rawMaterials[0] as RawMaterial,
          material: null,
        };
      }

      return emptyResult;
    } catch (err) {
      console.error('Error looking up material:', err);
      return emptyResult;
    }
  }, []);

  const clearScan = useCallback(() => {
    setLastScan(null);
    inputBuffer.current = '';
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isScanning,
    lastScan,
    error,
    inputRef,
    handleKeyboardScan,
    lookupMaterial,
    clearScan,
    clearError,
  };
}
